import { useEffect, useRef } from 'react';

import { registerCommands, updateCommands } from '../register-commands';
import type { Command } from '../types';

interface UseRegisterCommandsOptions {
    /** The commands to register */
    commands: Command[];
    /**
     * Dependency values that trigger a full unregister→re-register cycle.
     * Only needed for **structural** changes (e.g., the set of command IDs changes).
     * Closure freshness (enabled guards, onSelect callbacks) is handled automatically.
     */
    commandKey?: readonly unknown[];
    /** Whether commands should be registered. Defaults to false */
    enabled?: boolean | undefined;
}

/**
 * Register commands that auto-unregister when the component unmounts or when
 * `commandKey` changes. Similar to React Query's useQuery API.
 *
 * Closure freshness is handled automatically — `enabled` and `onSelect` always
 * use the latest values without needing to list their dependencies in `commandKey`.
 * Registration is disabled by default; pass `enabled: true`
 * to opt in.
 *
 * @example
 * useRegisterCommands({
 *     commands: [
 *         { id: 'asset:export', keys: ['O', 'E'], ... },
 *     ],
 *     commandKey: [assetType], // only when command IDs change
 *     enabled: featureFlag,
 * });
 */
export const useRegisterCommands = ({
    commands,
    commandKey = [],
    enabled = false,
}: UseRegisterCommandsOptions): void => {
    const commandsRef = useRef(commands);

    useEffect(() => {
        commandsRef.current = commands;
    });

    const key = JSON.stringify(commandKey);

    // Register/unregister commands when structural deps change
    useEffect(() => {
        if (!enabled) {
            return;
        }
        if (commandsRef.current.length === 0) {
            return;
        }
        return registerCommands(commandsRef.current);
    }, [key, enabled]);

    // Keep liveCommands in sync with fresh closures
    // so hotkey handlers always read the latest enabled/onSelect
    useEffect(() => {
        if (enabled) {
            updateCommands(commands);
        }
    });
};
